import noUiSlider, { API, PipsMode } from 'nouislider';
import { debounce } from '@utils/debounce';
import { usePieChart } from '@features/Shared/PieChart/js/pie-chart';
import { useBarChart } from '@features/Shared/BarChart/js/bar-chart';

const useTempoBlock = () => {
  interface SliderElement extends HTMLElement {
    noUiSlider: API;
  }

  const { setupPieChart } = usePieChart();
  const { setupBarChart } = useBarChart();

  const MARQUEE_SPEED = 0.6;
  const SPEED_VALUE = 12;
  const TEMPO_GAP = 6;
  const COMPETITOR_GAP = 6;
  const DURATION_TO_STOP_MARQUEE = 60000 * 5; // 5 minutes

  let tempoDots: HTMLLIElement[] = [];
  let competitorDots: HTMLLIElement[] = [];

  // Will hold controllers with a cancel method to stop the marquee animation
  let tempoController: { cancel: () => void };
  let competitorController: { cancel: () => void };

  const initSlider = (): API | null => {
    const sliderEl = document.querySelector<SliderElement>(
      '.tempo-block-speed-tool__slider'
    );

    if (!sliderEl) {
      return null;
    }

    return noUiSlider.create(sliderEl, {
      start: [SPEED_VALUE],
      range: { min: 7, max: 17 },
      tooltips: [{ to: (value: number): string => value.toFixed(1) }],
      pips: { mode: PipsMode.Range, density: 10 },
    });
  };

  const randomIntFromInterval = (min: number, max: number) => {
    return Math.floor(Math.random() * (max - min + 1) + min);
  };

  const updatePrecision = (speed: number) => {
    const dev = Math.floor(speed / 2);

    // Tempo dots padding
    tempoDots.forEach((dot: HTMLLIElement) => {
      const diff = randomIntFromInterval(0, Math.floor(dev / 2)) * (randomIntFromInterval(0, 1) ? 1 : -1);
      const left = TEMPO_GAP + diff;
      const right = TEMPO_GAP - diff;
      
      dot.style.paddingLeft = `${left}px`;
      dot.style.paddingRight = `${right}px`;
    });

    // Competitor dots padding
    competitorDots.forEach(dot => {
      const diff = randomIntFromInterval(0, dev) * (randomIntFromInterval(0, 1) ? 1 : -1);
      const left = COMPETITOR_GAP + diff;
      const right = COMPETITOR_GAP - diff;
      
      dot.style.paddingLeft = `${left}px`;
      dot.style.paddingRight = `${right}px`;
    });
  };

  // Planting precision
  const initPlantingPrecision = () => {
    const tempoListEl = document.querySelector('.tempo-block-planting-precision__list--tempo') as HTMLElement;
    const competitorListEl = document.querySelector('.tempo-block-planting-precision__list--competitor') as HTMLElement;

    if (!tempoListEl || !competitorListEl) {
      return;
    }

    function buildMarquee(listEl: HTMLElement, prefix: string, baseGap: number, collectArr: HTMLElement[], startSpeed: number) {
      const marquee = listEl.parentElement as HTMLElement;
      
      marquee.style.overflow = 'hidden';
      listEl.innerHTML = '';

      // Single dot template
      const template = document.createElement('li');
      
      template.className = 'tempo-block-planting-precision__dot';
      template.innerHTML = '<span>•</span>';
      template.style.position = 'absolute';
      template.style.top = '0';
      template.style.paddingLeft = `${baseGap}px`;
      template.style.paddingRight = `${baseGap}px`;

      // Measure
      marquee.appendChild(template);
      const dotWidth = template.offsetWidth;
      marquee.removeChild(template);

      const spacing = dotWidth + baseGap;
      const count = Math.ceil(marquee.clientWidth / spacing) + 1;

      // Clone & position
      for (let i = 0; i < count; i++) {
        const dot = template.cloneNode(true) as HTMLLIElement;
        dot.id = `${prefix}${i}`;
        dot.style.left = `${i * spacing}px`;
        listEl.appendChild(dot);
        collectArr.push(dot);
      }

      const totalWidth = spacing * count;
      let offset = 0;
      let frameSpeed = startSpeed;
      let frameId: number;

      const step = () => {
        offset = (offset + frameSpeed) % totalWidth;
        collectArr.forEach((dot, i) => {
          const x = ((i * spacing) - offset + totalWidth) % totalWidth;
          dot.style.left = `${x}px`;
        });
        frameId = requestAnimationFrame(step);
      };
      
      frameId = requestAnimationFrame(step);

      return {
        cancel: () => {
          if (frameId) {
            cancelAnimationFrame(frameId);
          }
        }
      };
    }

    // Build both marquees
    tempoController = buildMarquee(tempoListEl, 'tempo', TEMPO_GAP, tempoDots, MARQUEE_SPEED);
    competitorController = buildMarquee(competitorListEl, 'comp', COMPETITOR_GAP, competitorDots, MARQUEE_SPEED);

    // When the marquee has been running for 5 seconds, set speed to 0 to stop animation and save resources
    setTimeout(() => {      
      tempoController.cancel();
      competitorController.cancel();
    }, DURATION_TO_STOP_MARQUEE);
  };

  // Skips and doubles
  const updatePieCharts = (speed: number) => {
    const max = 8200;
    const tempoChart = document.querySelector(".tempo-block-chart--tempo") as HTMLElement;
    const compChart = document.querySelector(".tempo-block-chart--competitor") as HTMLElement;
    const skipsTempo = Math.round(1.2168 * Math.pow(speed, 4) - 56.956 * Math.pow(speed, 3) + 971.04 * Math.pow(speed, 2) - 7119.2 * speed + 19189);
    const skipsComp = Math.round(5.7899 * Math.pow(speed, 3) - 110.17 * Math.pow(speed, 2) + 754.35 * speed - 1397.3);

    setupPieChart(tempoChart, skipsTempo, max);
    setupPieChart(compChart, skipsComp, max);
  };

  // Yield loss percent
  const updateBarCharts = (speed: number) => {
    const max = 10; 
    const tempoBarChart = document.querySelector(".tempo-block-chart--tempo-loss") as HTMLElement;
    const compBarChart = document.querySelector(".tempo-block-chart--competitor-loss") as HTMLElement;

    const lossTempo = parseFloat((0.0016 * Math.pow(speed, 4) - 0.0747 * Math.pow(speed, 3) + 1.2704 * Math.pow(speed, 2) - 9.2811 * speed + 25.281).toFixed(1));
    const lossComp = parseFloat((0.0044 * Math.pow(speed, 3) - 0.0665 * Math.pow(speed, 2) + 0.2852 * speed + 0.6864).toFixed(1));

    setupBarChart(tempoBarChart, lossTempo, max);
    setupBarChart(compBarChart, lossComp, max);
  };

  // Hectares planted per 10h day
  const updatePlantedNumber = (speed: number) => {
    const plantedNumberElements = document.querySelectorAll('.tempo-block-planted-number');
    const value = Math.round(speed * 4.57).toString().padStart(3, '0');

    plantedNumberElements.forEach((element, i) => {
      element.textContent = value[i] ?? '';
    });
  }

  // Initialize the tempo block and its components
  const slider = initSlider();
  const initSpeed = slider ? parseFloat(<string>slider.get()) : SPEED_VALUE;

  // Initialize the planting precision
  initPlantingPrecision();
  updatePrecision(initSpeed);

  // Initialize pie charts
  updatePieCharts(initSpeed);

  // Initialize bar charts
  updateBarCharts(initSpeed);
  
  // Initialize planted number section
  updatePlantedNumber(initSpeed);

  // Add event listener for window resize
  window.addEventListener('resize', debounce(() => {
    initPlantingPrecision();
  }, 300));

  // Add event listener for showing tempo-block-infobox infoIcon on hover or click
  const infoIcon = document.querySelectorAll('.tempo-block-infobox__tip');
  if (infoIcon) {
    infoIcon.forEach(icon => {
      const tooltipText = icon.closest(".tempo-block-infobox")?.querySelector('.tempo-block-infobox__tooltip-text');

      // Show tooltip on mouse enter
      icon.addEventListener('mouseenter', () => {
        tooltipText?.classList.add('tempo-block-infobox__tooltip-text--visible');
      });

      // Hide tooltip on mouse leave
      icon.addEventListener('mouseleave', () => {
        tooltipText?.classList.remove('tempo-block-infobox__tooltip-text--visible');
      });

      // Toggle tooltip visibility on click
      icon.addEventListener('click', () => {
        tooltipText?.classList.toggle('tempo-block-infobox__tooltip-text--visible');
      });
    });
  }

  if (slider) {
    slider.on('update', ([speed]) => {
      updatePrecision(parseFloat(<string>speed));
      updatePieCharts(<number>speed);
      updateBarCharts(<number>speed);
      updatePlantedNumber(<number>speed);
    });
  }
};

document.addEventListener('DOMContentLoaded', useTempoBlock);
